import React, { useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Image as ImageIcon, X, Loader2, Tag } from "lucide-react";
import { GlassField, GlassButton } from "@/components/GlassBits";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX = 20;

/** Formulario para publicar o editar un producto (hasta 20 fotos). */
export const ProductForm = ({ product, onSaved, onClose }) => {
  const [title, setTitle] = useState(product?.title || "");
  const [price, setPrice] = useState(product?.price != null ? String(product.price) : "");
  const [acceptsCredits, setAcceptsCredits] = useState(product?.accepts_credits ?? true);
  const [description, setDescription] = useState(product?.description || "");
  const [status, setStatus] = useState(product?.status || "disponible");
  const [media, setMedia] = useState(product?.media || []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(0);

  const attach = async (files) => {
    if (!files?.length) return;
    const room = MAX - media.length;
    if (room <= 0) return toast.error(`Máximo ${MAX} fotos por producto`);
    const list = Array.from(files).slice(0, room);
    setUploading(list.length);
    const results = await Promise.all(list.map(async (f) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await userApi.post("/community/media", fd);
        return data;
      } catch (e) {
        toast.error(apiError(e, `No se pudo subir ${f.name}`));
        return null;
      } finally {
        setUploading((n) => n - 1);
      }
    }));
    setMedia((m) => [...m, ...results.filter(Boolean)].slice(0, MAX));
  };

  const save = async () => {
    const value = Number(String(price).replace(",", "."));
    if (title.trim().length < 3) return toast.error("Ponle un título al producto");
    if (!Number.isFinite(value) || value < 0) return toast.error("Escribe un precio válido");
    setBusy(true);
    const body = { title: title.trim(), price: value, description: description.trim(),
                   media_ids: media.map((m) => m.media_id), status,
                   accepts_credits: acceptsCredits };
    try {
      const { data } = product
        ? await userApi.patch(`/market/products/${product.product_id}`, body)
        : await userApi.post("/market/products", body);
      toast.success(product ? "Producto actualizado" : "Producto publicado");
      onSaved?.(data);
    } catch (e) { toast.error(apiError(e, "No se pudo guardar")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(3,0,0,0.75)" }} data-testid="product-form">
      <div className="glass-card w-full max-w-lg p-5 max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-white font-semibold">
            <Tag className="w-4 h-4 text-red-400" />
            {product ? "Editar producto" : "Publicar producto"}
          </div>
          <button data-testid="product-form-close" onClick={onClose} className="text-white/60">
            <X className="w-4 h-4" />
          </button>
        </div>

        <GlassField label="Título" value={title} data-testid="product-title-input"
          placeholder="Ej. Bocina bluetooth" onChange={(e) => setTitle(e.target.value)} />

        <GlassField label="Precio (MXN)" value={price} inputMode="decimal" className="mt-3"
          data-testid="product-price-input" placeholder="0.00"
          onChange={(e) => setPrice(e.target.value)} />

        <button type="button" data-testid="product-accepts-credits-toggle"
          onClick={() => setAcceptsCredits((v) => !v)}
          className="mt-3 w-full glass-card px-4 py-3 flex items-center justify-between text-left">
          <span>
            <span className="block text-sm text-white">Acepto pago con créditos</span>
            <span className="block text-[11px] text-white/50 mt-0.5">
              Recibes el 85% en créditos cuando el administrador aprueba el trato
            </span>
          </span>
          <span className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${acceptsCredits ? "bg-amber-500/80" : "bg-white/15"}`}>
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${acceptsCredits ? "left-5" : "left-1"}`} />
          </span>
        </button>

        <label className="block mt-3">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-white/55 mb-1.5">
            Descripción
          </span>
          <textarea value={description} rows={4} data-testid="product-desc-input"
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Cuenta cómo está, qué incluye y dónde lo entregas"
            className="glass-field w-full px-3.5 py-2.5 text-[15px] text-white placeholder-white/35 rounded-xl outline-none resize-none" />
        </label>

        <div className="mt-3">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-white/55 mb-1.5">
            Estado
          </span>
          <div className="flex gap-2">
            {["disponible", "reservado", "vendido"].map((s) => (
              <button key={s} data-testid={`product-status-${s}`} onClick={() => setStatus(s)}
                className={`glass-btn ${status === s ? "glass-btn-red" : ""} px-3 py-2 rounded-xl text-xs capitalize`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-[0.14em] text-white/55">
              Fotos ({media.length}/{MAX})
            </span>
            <label className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 cursor-pointer">
              <ImageIcon className="w-3.5 h-3.5" /> Agregar
              <input type="file" accept="image/*" multiple className="hidden"
                data-testid="product-photos-input" onChange={(e) => attach(e.target.files)} />
            </label>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="product-photos-preview">
            {media.map((m) => (
              <div key={m.media_id} className="relative">
                <img src={`${BACKEND}${m.url}`} alt={m.name}
                  className="w-20 h-20 object-cover rounded-lg border border-white/12" />
                <button onClick={() => setMedia((x) => x.filter((y) => y.media_id !== m.media_id))}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            {uploading > 0 && (
              <div className="w-20 h-20 rounded-lg border border-white/12 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-red-400" />
              </div>
            )}
          </div>
        </div>

        <GlassButton tone="red" className="mt-5" loading={busy} data-testid="product-save-btn"
          onClick={save}>{product ? "Guardar cambios" : "Publicar en el mercado"}</GlassButton>
      </div>
    </div>
  );
};

export default ProductForm;
